import { useState, useEffect } from 'react';

/**
 * Hook to detect keyboard height and visibility on mobile devices
 * @returns {Object} { keyboardHeight, isKeyboardVisible }
 */
const useKeyboard = () => {
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    useEffect(() => {
        let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
        let initialWindowHeight = window.innerHeight;

        const handleResize = () => {
            const currentViewportHeight = window.visualViewport?.height || window.innerHeight;

            // Calculate keyboard height by comparing viewport changes
            const heightDifference = initialWindowHeight - currentViewportHeight;
            
            if (heightDifference > 150) { // Threshold to detect keyboard (150px minimum)
                setKeyboardHeight(heightDifference);
                setIsKeyboardVisible(true);
            } else {
                setKeyboardHeight(0);
                setIsKeyboardVisible(false);
            }
        };

        const handleVisualViewportChange = () => {
            if (window.visualViewport) {
                const currentHeight = window.visualViewport.height;
                const heightDifference = initialViewportHeight - currentHeight;
                
                if (heightDifference > 150) {
                    setKeyboardHeight(heightDifference);
                    setIsKeyboardVisible(true);
                } else {
                    setKeyboardHeight(0);
                    setIsKeyboardVisible(false);
                }
            }
        };

        // Listen to window resize events
        window.addEventListener('resize', handleResize);
        
        // Listen to visual viewport changes (more reliable on modern browsers)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleVisualViewportChange);
        }

        // Also listen to focus/blur events on input elements as fallback
        const handleFocusIn = (e) => {
            if (e.target.matches('input, textarea, select, [contenteditable]')) {
                // Small delay to allow keyboard to appear
                setTimeout(handleResize, 300);
            }
        };

        const handleFocusOut = () => {
            // Small delay to allow keyboard to disappear
            setTimeout(handleResize, 300);
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
            }
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    return {
        keyboardHeight,
        isKeyboardVisible
    };
};

export default useKeyboard;